import {
  Colors,
  Icon,
  InputGroup,
  NumericInput,
  Tooltip,
} from '@blueprintjs/core';
import AccountSelect from 'Components/Generic/BOSFilterSelect/AccountSelect';
import ExpenseCodeSelect from 'Components/Generic/BOSFilterSelect/ExpenseCodeSelect';
import { showSaveSuccessToast } from 'Components/Generic/BOSToasterUtil';
import AddNewButton from 'Components/Generic/Buttons/AddNewButton';
import ButtonFooter from 'Components/Generic/Buttons/ButtonFooter';
import DeleteButton from 'Components/Generic/Buttons/DeleteButton';
import SaveButton from 'Components/Generic/Buttons/SaveButton';
import UndoButton from 'Components/Generic/Buttons/UndoButton';
import ChargeAllocationStatusLabel from 'Components/Generic/ChargeAllocations/ChargeAllocationStatusLabel';
import ErrorMessage from 'Components/Generic/Misc/ErrorMessage';
import AppData from 'Data/AppData';
import ChargeAllocationData from 'Data/ChargeAllocationData';
import { user_roles } from 'Data/Enum';
import GenericTableData from 'Data/GenericTableData';
import DataManager from 'Lib/DataManager';
import ConditionalWrapper from 'Lib/hoc/ConditionalWrapper';
import {
  CHARGE_ALLOCATION_STATUS,
  getChargeAllocationStatus,
} from 'Pages/Transactions/Enum';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import {
  areEquivalent,
  formatMoney,
  isUserAuthorized,
  roundToHundredth,
} from 'Util';
import styles from './ChartfieldList.module.css';

let DM = new DataManager();

const ChartfieldList = ({
  transaction,
  lineItems,
  isNew = false,
  editable = false,
  setEditState = () => {},
  compact = false,
}) => {
  let ThisChargeAllocationData = useRef(
    GenericTableData.getTableDataBus('CH_ALL', 'charge_allocations'),
  );
  let CurrentChargeAllocationData = ThisChargeAllocationData.current;

  const [disableSave, setDisableSave] = useState(false);
  const [hasBeenUpdated, setHasBeenUpdated] = useState(false);
  const [chargeAllocationList, setChargeAllocationList] = useState([]);
  const [deletedChargeAllocations, setDeletedChargeAllocations] = useState([]);
  const [purchaseTotal, setPurchaseTotal] = useState();
  const [errorMessageList, setErrorMessageList] = useState([]);
  const transactionRef = useRef();
  const lineItemsRef = useRef();

  useEffect(() => {
    DM.add(
      CurrentChargeAllocationData.registerListener(
        'BLANK_RECORD_LOADED',
        onBlankChargeAllocationLoaded,
      ),
      CurrentChargeAllocationData,
    );
    DM.add(
      ChargeAllocationData.registerListener(
        'CHARGES_SAVED_FOR_TRANSACTION',
        onChargeAllocationsUpdated,
      ),
      ChargeAllocationData,
    );
    DM.add(
      CurrentChargeAllocationData.registerListener(
        'LIST_LOADED',
        onChargeAllocationListLoaded,
      ),
      CurrentChargeAllocationData,
    );
    DM.add(
      CurrentChargeAllocationData.registerListener(
        'RECORDS_LOAD_ERROR',
        onChargeAllocationListError,
      ),
      CurrentChargeAllocationData,
    );
    DM.add(
      CurrentChargeAllocationData.registerListener(
        'RECORD_UPDATE_ERROR',
        onUpdateError,
      ),
      CurrentChargeAllocationData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  useEffect(() => {
    setEditState(undefined);
  }, []);

  useEffect(() => {
    setEditState(undefined);
  }, [editable]);

  useEffect(() => {
    if (transaction) {
      transactionRef.current = transaction;
      if (transaction.line_items) {
        lineItemsRef.current = transaction.line_items;
      }
      if (
        transaction.purchase_total &&
        !isNaN(+transaction.purchase_total) &&
        transaction.category === 'EXPENSE'
      ) {
        setPurchaseTotal(roundToHundredth(transaction.purchase_total * -1));
      }
      fetchChargeAllocations();
    }
  }, [transaction]);

  useEffect(() => {
    lineItemsRef.current = lineItems;
  }, [lineItems]);

  useEffect(() => {
    let errorMessages = [];
    let anyZero = false;
    let missingExpenseCode = false;
    let missingAccountId = false;
    let currentTotal = roundToHundredth(
      chargeAllocationList.reduce((acc, ca) => {
        if (ca.display_amount == 0) {
          anyZero = true;
        }
        if (!ca.assigned_charge_account_id) {
          missingAccountId = true;
        }
        if (!ca.assigned_expense_code_id) {
          missingExpenseCode = true;
        }
        return ca.display_amount ? acc + +ca.display_amount : acc;
      }, 0),
    );
    if (missingAccountId) {
      errorMessages.push('Each line item must have an account assigned to it.');
    }
    if (missingExpenseCode) {
      errorMessages.push(
        'Each line item must have an expense code assigned to it.',
      );
    }
    if (anyZero) {
      errorMessages.push('Each line item should have a non-zero amount.');
    }
    if (purchaseTotal && currentTotal !== purchaseTotal) {
      errorMessages.push(
        'All line item amounts must add up to the purchase total.',
      );
    }
    setErrorMessageList(errorMessages);
  }, [chargeAllocationList]);

  const onUpdateError = () => {
    setDisableSave(false);
  };

  const fetchChargeAllocations = () => {
    if (transactionRef?.current?.id) {
      CurrentChargeAllocationData.getRecordList(
        {
          where: {
            '*parent_transaction_id': transactionRef.current.id,
          },
          _sort: 'id',
        },
        true,
      );
    }
  };

  const getNewChargeAllocationFields = (getAmountFromTrans = false) => {
    let lineItemList =
      lineItemsRef?.current || transactionRef.current?.line_items;
    let lineItemDescriptionString = lineItemList
      ? lineItemList
          .map((lineItem) => {
            return lineItem.description;
          })
          .join(', ')
      : undefined;
    let isExpense = transactionRef.current.category === 'EXPENSE';
    let amount = getAmountFromTrans ? transactionRef.current.purchase_total : 0;
    let displayAmount = amount * (isExpense ? -1 : 1);
    return {
      parent_transaction_id: transactionRef.current.id,
      category: transactionRef.current.category,
      status: isExpense
        ? CHARGE_ALLOCATION_STATUS.PENDING.key
        : CHARGE_ALLOCATION_STATUS.OPEN.key,
      amount: amount,
      display_amount: displayAmount,
      date_posted: transactionRef.current.date_purchased || new Date(),
      allocated_by: AppData.userID(),
      description: lineItemDescriptionString
        ? lineItemDescriptionString
        : transactionRef.current?.notes
        ? `${transactionRef.current.notes}`
        : '',
    };
  };

  const onBlankChargeAllocationLoaded = (newChargeAllocation) => {
    setHasBeenUpdated(true);
    let chargeAllocation = {
      ...newChargeAllocation,
      ...getNewChargeAllocationFields(),
    };
    setChargeAllocationList((prevItems) => [...prevItems, chargeAllocation]);
  };

  const onChargeAllocationListLoaded = (payload) => {
    if (payload) {
      setDisableSave(false);
      setHasBeenUpdated(false);
      setDeletedChargeAllocations([]);
      setErrorMessageList([]);
      if (payload.count) {
        setChargeAllocationList([...payload.records]);
      } else if (
        lineItemsRef?.current?.length ||
        transaction?.line_items?.length
      ) {
        setChargeAllocationList([getNewChargeAllocationFields(true)]);
      }
    }
  };

  const onChargeAllocationListError = () => {};

  const updateItemValue = (itemToUpdate, updateField, updatedValue) => {
    const updatedArray = chargeAllocationList.map((item) => {
      if (item === itemToUpdate) {
        let originalItem = { ...item };
        let updatedItem = { ...item, [updateField]: updatedValue };
        if (!areEquivalent(originalItem, updatedItem)) {
          setHasBeenUpdated(true);
        }
        return updatedItem;
      }
      return item;
    });
    setChargeAllocationList(updatedArray);
  };

  const updateItemPercent = (itemToUpdate, newPercent) => {
    let newAmount =
      purchaseTotal && roundToHundredth((+newPercent / 100) * purchaseTotal);
    updateItemAmount(itemToUpdate, newAmount);
  };

  const updateItemAmount = (itemToUpdate, newAmount) => {
    if (+itemToUpdate.display_amount === +newAmount) {
      return;
    }
    setHasBeenUpdated(true);
    setErrorMessageList([]);
    let roundedNewAmount = roundToHundredth(+newAmount);
    let purchaseTotalWithoutLast = 0;
    const updatedArray = chargeAllocationList.map((item, index) => {
      if (index !== chargeAllocationList.length - 1) {
        purchaseTotalWithoutLast +=
          item === itemToUpdate ? roundedNewAmount : +item.display_amount;
      }
      if (item === itemToUpdate) {
        return { ...item, display_amount: roundedNewAmount };
      }
      return { ...item };
    });

    if (purchaseTotal) {
      let difference = (purchaseTotal - purchaseTotalWithoutLast).toFixed(2);
      updatedArray[updatedArray.length - 1].display_amount = difference || 0;
    }

    setChargeAllocationList(updatedArray);
  };

  const markItemForDelete = (caToDelete) => {
    setHasBeenUpdated(true);
    setChargeAllocationList((prevItems) =>
      prevItems.filter((item) => item !== caToDelete),
    );
    if (caToDelete.id) {
      setDeletedChargeAllocations((prevList) => [...prevList, caToDelete]);
    }
  };

  const deleteChargeAllocations = () => {
    if (deletedChargeAllocations.length > 0) {
      CurrentChargeAllocationData.deleteRecords(
        deletedChargeAllocations.map((ca) => ca.id),
      );
    }
  };

  const saveClicked = () => {
    setDisableSave(true);
    let isExpense = transaction.category === 'EXPENSE';
    ChargeAllocationData.saveChargesForTransaction(
      chargeAllocationList.map(({ display_amount, ...ca }) => ({
        ...ca,
        amount: display_amount * (isExpense ? -1 : 1),
        ...(ca.status === CHARGE_ALLOCATION_STATUS.CLEARED.key &&
          !ca.reconciled_by && { reconciled_by: AppData.userID() }),
      })),
      transaction,
    );
    deleteChargeAllocations();
  };

  const onChargeAllocationsUpdated = (payload) => {
    if (
      payload?.length &&
      payload[0].parent_transaction_id === transactionRef?.current.id
    ) {
      showSaveSuccessToast(`Chartfield${payload.length > 1 ? 's' : ''} Saved`);
      setEditState(false);
      fetchChargeAllocations();
    }
  };

  const undoChanges = () => {
    fetchChargeAllocations();
  };

  let headers = (
    <tr>
      <td className={styles.table_header_cell}>Act/Proj</td>
      <td className={styles.table_header_cell}>Expense Code</td>
      <td className={styles.table_header_cell}>Description</td>
      {purchaseTotal !== undefined && (
        <td style={{ width: 100 }} className={styles.table_header_cell}>
          Percent
        </td>
      )}
      <td
        style={{ width: 100, textAlign: 'right' }}
        className={styles.table_header_cell}>
        Amount
      </td>
      <td style={{ width: 100 }} className={styles.table_header_cell}>
        Status
      </td>
      {editable && <td style={{ width: 20 }}></td>}
    </tr>
  );

  let filteredChargeAllocationList = editable
    ? chargeAllocationList
    : chargeAllocationList.filter((ca) => ca.id);

  let lastLineMessage = `Auto-calculated. ${
    chargeAllocationList.length > 1
      ? 'Edit the values above.'
      : 'Add additional charge allocations if you want to adjust amounts.'
  } `;

  let chargeAllocationTableContent = filteredChargeAllocationList.map(
    (chargeAllocation, index) => {
      return (
        <tr key={index}>
          <td
            style={{
              textAlign: 'center',
            }}>
            {editable && (
              <AccountSelect
                orgId={transaction.parent_organization_id}
                category={transaction.category}
                defaultSelectedId={chargeAllocation.assigned_charge_account_id}
                onSelectAccount={(act) => {
                  updateItemValue(
                    chargeAllocation,
                    'assigned_charge_account_id',
                    act?.id || null,
                  );
                }}
              />
            )}
            {!editable && chargeAllocation.charge_account
              ? `${chargeAllocation.charge_account.code} ${chargeAllocation.charge_account.name}`
              : ''}
          </td>
          <td
            style={{
              textAlign: 'center',
            }}>
            {editable && (
              <ExpenseCodeSelect
                defaultSelectedId={chargeAllocation.assigned_expense_code_id}
                onSelectExpenseCode={(ec) => {
                  updateItemValue(
                    chargeAllocation,
                    'assigned_expense_code_id',
                    ec?.id || null,
                  );
                }}
              />
            )}
            {!editable && chargeAllocation.expense_code
              ? `${chargeAllocation.expense_code.expense_code} ${chargeAllocation.expense_code.description}`
              : ''}
          </td>
          <td
            style={{
              textAlign: 'center',
            }}>
            {editable && (
              <InputGroup
                type='text'
                value={chargeAllocation.description || ''}
                onChange={(event) => {
                  updateItemValue(
                    chargeAllocation,
                    'description',
                    event.target.value,
                  );
                }}
              />
            )}
            {(!editable && chargeAllocation.description) || ''}
          </td>
          {purchaseTotal !== undefined && (
            <td style={{ textAlign: 'center' }}>
              {editable && (
                <ConditionalWrapper
                  condition={index === chargeAllocationList.length - 1}
                  wrapper={(children) => (
                    <Tooltip content={lastLineMessage}>{children}</Tooltip>
                  )}>
                  <NumericInput
                    asyncControl
                    readOnly={
                      transaction.category === 'EXPENSE' &&
                      chargeAllocationList.length > 1 &&
                      index === chargeAllocationList.length - 1
                    }
                    className={styles.right_icon}
                    fill
                    minorStepSize={0.01}
                    allowNumericCharactersOnly
                    buttonPosition={'none'}
                    rightElement={
                      <div
                        style={{
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          paddingRight: 12,
                        }}>
                        <Icon icon={'percentage'} color={Colors.GRAY1} />
                      </div>
                    }
                    clampValueOnBlur
                    selectAllOnFocus
                    value={
                      purchaseTotal && Math.abs(purchaseTotal) > 0
                        ? roundToHundredth(
                            (Math.abs(+chargeAllocation.display_amount) /
                              Math.abs(purchaseTotal)) *
                              100,
                          )
                        : 0
                    }
                    onValueChange={(valueAsNumber, valueAsString) =>
                      updateItemPercent(chargeAllocation, valueAsString)
                    }
                  />
                </ConditionalWrapper>
              )}
              {!editable &&
                (purchaseTotal && Math.abs(purchaseTotal) > 0
                  ? `${roundToHundredth(
                      (Math.abs(+chargeAllocation.display_amount) /
                        Math.abs(purchaseTotal)) *
                        100,
                    )} %`
                  : '-')}
            </td>
          )}
          <td style={{ textAlign: 'right' }}>
            {editable && (
              <ConditionalWrapper
                condition={index === chargeAllocationList.length - 1}
                wrapper={(children) => (
                  <Tooltip content={lastLineMessage}>{children}</Tooltip>
                )}>
                <NumericInput
                  asyncControl
                  readOnly={
                    transaction.category === 'EXPENSE' &&
                    purchaseTotal &&
                    chargeAllocationList.length > 1 &&
                    index === chargeAllocationList.length - 1
                  }
                  fill
                  style={{ textAlign: 'right', width: '100%' }}
                  leftIcon={'dollar'}
                  buttonPosition={'none'}
                  minorStepSize={0.01}
                  allowNumericCharactersOnly
                  clampValueOnBlur
                  selectAllOnFocus
                  value={chargeAllocation.display_amount || 0}
                  onValueChange={(valueAsNumber, valueAsString) =>
                    updateItemAmount(chargeAllocation, valueAsString)
                  }
                />
              </ConditionalWrapper>
            )}
            {!editable && (
              <div
                className={styles.money_cell}
                style={{ display: 'flex', alignItems: 'center' }}>
                <Icon
                  size={12}
                  icon={'dollar'}
                  style={{ marginRight: 'auto' }}
                />
                {formatMoney(chargeAllocation.display_amount)}
              </div>
            )}
          </td>
          <td style={{ textAlign: 'center' }}>
            <ChargeAllocationStatusLabel
              readOnly={
                !editable ||
                !isUserAuthorized({
                  roles: [user_roles.ADMINISTRATOR, user_roles.RECONCILER],
                })
              }
              selectedStatus={getChargeAllocationStatus(
                chargeAllocation.status,
              )}
              onSelectStatus={(nextStatus) => {
                updateItemValue(chargeAllocation, 'status', nextStatus.key);
              }}
            />
          </td>
          {editable && (
            <td>
              {index > 0 && (
                <DeleteButton
                  miniVersion
                  useXIcon
                  onDeleteItem={() => markItemForDelete(chargeAllocation)}
                  tooltipText={'Remove Chartfield'}
                />
              )}
            </td>
          )}
        </tr>
      );
    },
  );

  let currentTotal = roundToHundredth(
    chargeAllocationList.reduce((acc, ca) => {
      return ca.display_amount ? acc + +ca.display_amount : acc;
    }, 0),
  );

  let errorMessageComponent =
    editable &&
    hasBeenUpdated &&
    errorMessageList.map((errorMessage, index) => {
      return (
        <ErrorMessage
          key={index}
          message={errorMessage}
          small
          style={{ marginLeft: 20 }}
        />
      );
    });

  let totalDoesNotAddUp = purchaseTotal && currentTotal !== purchaseTotal;

  let totalRow = (
    <tr>
      <td colSpan={purchaseTotal !== undefined ? 3 : 2}>
        {errorMessageComponent}
      </td>
      <td className={styles.total_label_cell}>Total:</td>
      <td className={`${styles.table_total_cell} ${styles.money_cell}`}>
        <div
          className={totalDoesNotAddUp ? styles.error_field : ''}
          style={{
            display: 'flex',
            alignItems: 'center',
          }}>
          <Icon size={14} icon={'dollar'} style={{ marginRight: 'auto' }} />
          <ConditionalWrapper
            condition={totalDoesNotAddUp}
            wrapper={(children) => (
              <Tooltip content={'Total does not equal transaction total.'}>
                {children}
              </Tooltip>
            )}>
            {formatMoney(currentTotal)}
          </ConditionalWrapper>
        </div>
      </td>
      <td className={`${styles.trans_total_cell} ${styles.money_cell}`}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {purchaseTotal ? `/ ${formatMoney(purchaseTotal)}` : ''}
        </div>
      </td>
    </tr>
  );

  const newButton = editable && (
    <AddNewButton
      clickHandler={CurrentChargeAllocationData.getBlank}
      buttonText='Add Account'
    />
  );

  const undoButton = !isNew && editable && hasBeenUpdated && (
    <UndoButton clickHandler={undoChanges} />
  );

  const saveButton = editable && hasBeenUpdated && (
    <SaveButton
      disabled={errorMessageList?.length > 0 || disableSave}
      clickHandler={saveClicked}
    />
  );

  const footer = editable && (
    <ButtonFooter marginTop={0}>
      {undoButton}
      {newButton}
      {saveButton}
    </ButtonFooter>
  );

  return (
    <>
      <table
        className={`${
          compact ? styles.chartfield_table_compact : styles.chartfield_table
        } ${editable ? styles.chartfield_table_edit_mode : ''}`}>
        <thead>{headers}</thead>
        <tbody>{chargeAllocationTableContent}</tbody>
        <tfoot>{totalRow}</tfoot>
      </table>
      {footer}
    </>
  );
};

ChartfieldList.propTypes = {
  transaction: PropTypes.object,
  lineItems: PropTypes.array,
  isNew: PropTypes.bool,
  editable: PropTypes.bool,
  setEditState: PropTypes.func,
  compact: PropTypes.bool,
};

export default ChartfieldList;
